import { useState, useEffect, useRef } from "react";
import { useAuth, API } from "../App";
import Navigation from "../components/Navigation";
import axios from "axios";
import {
  Heart, Star, MessageCircle, Share2, Play, Pause,
  Volume2, VolumeX, ChevronUp, ChevronDown, User
} from "lucide-react";
import { toast } from "sonner";

export default function Reels() {
  const { user } = useAuth();
  const [reels, setReels] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(true);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const videoRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    fetchReels();
  }, []);

  useEffect(() => {
    if (videoRef.current) {
      if (playing) {
        videoRef.current.play();
      } else {
        videoRef.current.pause();
      }
    }
  }, [playing, currentIndex]);

  const fetchReels = async () => {
    try {
      const response = await axios.get(`${API}/reels`);
      setReels(response.data);
    } catch (error) {
      console.error("Failed to fetch reels:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async (reelId) => {
    try {
      const response = await axios.get(`${API}/reels/${reelId}/comments`);
      setComments(response.data);
    } catch (error) {
      console.error("Failed to fetch comments:", error);
    }
  };

  const handleScroll = (direction) => {
    if (direction === "up" && currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    } else if (direction === "down" && currentIndex < reels.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
    setPlaying(true);
  };

  const handleReact = async (reactionType) => {
    const reel = reels[currentIndex];
    try {
      const response = await axios.post(
        `${API}/reels/${reel.id}/react?reaction_type=${reactionType}`
      );
      
      // Update local state
      const updatedReels = [...reels];
      const field = `${reactionType}s`;
      if (response.data.action === "added") {
        updatedReels[currentIndex][field] = (updatedReels[currentIndex][field] || 0) + 1;
      } else {
        updatedReels[currentIndex][field] = Math.max(0, (updatedReels[currentIndex][field] || 0) - 1);
      }
      setReels(updatedReels);
    } catch (error) {
      toast.error("Failed to react");
    }
  };

  const handleComment = async () => {
    if (!newComment.trim()) return;
    
    const reel = reels[currentIndex];
    try {
      await axios.post(`${API}/reels/${reel.id}/comment?content=${encodeURIComponent(newComment)}`);
      toast.success("Comment added!");
      setNewComment("");
      fetchComments(reel.id);
      
      // Update comment count
      const updatedReels = [...reels];
      updatedReels[currentIndex].comments = (updatedReels[currentIndex].comments || 0) + 1;
      setReels(updatedReels);
    } catch (error) {
      toast.error("Failed to add comment");
    }
  };

  const toggleComments = () => {
    if (!showComments) {
      fetchComments(reels[currentIndex]?.id);
    }
    setShowComments(!showComments);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (reels.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
        <Navigation />
        <div className="flex flex-col items-center justify-center h-[70vh] px-4">
          <Play className="w-20 h-20 text-gray-300 mb-4" />
          <h2 className="text-2xl font-bold text-gray-700 mb-2">No Reels Yet</h2>
          <p className="text-gray-500 text-center">
            Be the first to share a motivational video reel!
          </p>
        </div>
      </div>
    );
  }

  const currentReel = reels[currentIndex];

  return (
    <div className="min-h-screen bg-black">
      {/* Top Navigation */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-b from-black/80 to-transparent p-4">
        <div className="flex items-center justify-between max-w-lg mx-auto">
          <h1 className="text-white font-bold text-xl">Reels</h1>
          <div className="flex gap-4">
            <button
              onClick={() => setMuted(!muted)}
              className="text-white p-2"
            >
              {muted ? <VolumeX className="w-6 h-6" /> : <Volume2 className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Reel Container */}
      <div
        ref={containerRef}
        className="h-screen w-full flex items-center justify-center relative"
        onClick={() => setPlaying(!playing)}
      >
        {/* Video */}
        {currentReel.video_url ? (
          <video
            ref={videoRef}
            src={currentReel.video_url}
            className="h-full w-full object-cover"
            loop
            muted={muted}
            playsInline
            autoPlay
          />
        ) : (
          <div className="h-full w-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
            <p className="text-white text-xl text-center px-8">{currentReel.caption}</p>
          </div>
        )}

        {/* Play/Pause Indicator */}
        {!playing && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/20">
            <Play className="w-20 h-20 text-white opacity-80" />
          </div>
        )}

        {/* User Info & Caption */}
        <div className="absolute bottom-20 left-4 right-20 text-white">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center overflow-hidden">
              {currentReel.user_photo ? (
                <img src={currentReel.user_photo} alt="" className="w-full h-full object-cover" />
              ) : (
                <User className="w-5 h-5 text-white" />
              )}
            </div>
            <span className="font-semibold">{currentReel.user_name}</span>
          </div>
          <p className="text-sm leading-relaxed">{currentReel.caption}</p>
          {currentReel.music && (
            <p className="text-xs mt-2 opacity-70">🎵 {currentReel.music}</p>
          )}
        </div>

        {/* Side Actions */}
        <div className="absolute right-4 bottom-32 flex flex-col gap-6">
          {/* Heart */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleReact("heart");
            }}
            className="flex flex-col items-center"
          >
            <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors">
              <Heart className="w-7 h-7 text-white" />
            </div>
            <span className="text-white text-xs mt-1">{currentReel.hearts || 0}</span>
          </button>

          {/* Star */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleReact("star");
            }}
            className="flex flex-col items-center"
          >
            <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors">
              <Star className="w-7 h-7 text-white" />
            </div>
            <span className="text-white text-xs mt-1">{currentReel.stars || 0}</span>
          </button>

          {/* Comments */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleComments();
            }}
            className="flex flex-col items-center"
          >
            <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors">
              <MessageCircle className="w-7 h-7 text-white" />
            </div>
            <span className="text-white text-xs mt-1">{currentReel.comments || 0}</span>
          </button>

          {/* Share */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              toast.info("Share feature coming soon!");
            }}
            className="flex flex-col items-center"
          >
            <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors">
              <Share2 className="w-7 h-7 text-white" />
            </div>
            <span className="text-white text-xs mt-1">{currentReel.shares || 0}</span>
          </button>
        </div>

        {/* Navigation Arrows */}
        {currentIndex > 0 && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleScroll("up");
            }}
            className="absolute top-1/4 left-1/2 -translate-x-1/2 text-white/50 hover:text-white"
          >
            <ChevronUp className="w-10 h-10" />
          </button>
        )}
        {currentIndex < reels.length - 1 && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleScroll("down");
            }}
            className="absolute bottom-1/4 left-1/2 -translate-x-1/2 text-white/50 hover:text-white"
          >
            <ChevronDown className="w-10 h-10" />
          </button>
        )}

        {/* Progress Dots */}
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex flex-col gap-1">
          {reels.slice(0, 10).map((_, idx) => (
            <div
              key={idx}
              className={`w-1 h-1 rounded-full transition-colors ${
                idx === currentIndex ? "bg-white" : "bg-white/30"
              }`}
            />
          ))}
        </div>
      </div>

      {/* Comments Panel */}
      {showComments && (
        <div
          className="fixed inset-x-0 bottom-0 bg-white rounded-t-3xl z-50 max-h-[60vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-4 border-b flex items-center justify-between">
            <h3 className="font-semibold text-lg">Comments ({currentReel.comments || 0})</h3>
            <button onClick={toggleComments} className="text-gray-500">✕</button>
          </div>
          
          <div className="overflow-y-auto max-h-[40vh] p-4 space-y-4">
            {comments.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No comments yet. Be the first!</p>
            ) : (
              comments.map((comment) => (
                <div key={comment.id} className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                    {comment.user_photo ? (
                      <img src={comment.user_photo} alt="" className="w-full h-full rounded-full object-cover" />
                    ) : (
                      <span className="text-amber-600 text-sm font-bold">{comment.user_name?.[0]}</span>
                    )}
                  </div>
                  <div>
                    <span className="font-semibold text-sm">{comment.user_name}</span>
                    <p className="text-gray-700 text-sm">{comment.content}</p>
                  </div>
                </div>
              ))
            )}
          </div>
          
          <div className="p-4 border-t flex gap-2">
            <input
              type="text"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Add a comment..."
              className="flex-1 px-4 py-2 rounded-full bg-gray-100 focus:outline-none focus:ring-2 focus:ring-amber-500"
              onKeyPress={(e) => e.key === "Enter" && handleComment()}
            />
            <button
              onClick={handleComment}
              disabled={!newComment.trim()}
              className="px-4 py-2 bg-amber-500 text-white rounded-full disabled:opacity-50"
            >
              Post
            </button>
          </div>
        </div>
      )}

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0">
        <Navigation />
      </div>
    </div>
  );
}
